'use client';

import React, { useState } from 'react';
import { useTradingData } from '@/hooks/useTradingData';
import { Shield, ShieldAlert, AlertTriangle, ArrowRightLeft } from 'lucide-react';

export const ModeIndicator: React.FC = () => {
  const { settings, switchMode } = useTradingData();
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [isSwitching, setIsSwitching] = useState<boolean>(false);

  const currentMode = settings?.mode || 'demo';
  const isLive = currentMode === 'live';

  const handleToggleClick = () => {
    if (!isLive) {
      // Switching from Demo to Live requires confirmation
      setShowConfirmModal(true);
    } else {
      // Switching back to Demo directly
      executeSwitch('demo');
    }
  };

  const executeSwitch = async (targetMode: 'demo' | 'live') => {
    setIsSwitching(true);
    await switchMode(targetMode);
    setIsSwitching(false);
    setShowConfirmModal(false);
  };

  return (
    <>
      <div className="flex items-center space-x-2">
        <button
          onClick={handleToggleClick}
          disabled={isSwitching}
          className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border font-semibold text-xs transition-all duration-200 shadow-md ${
            isLive
              ? 'bg-red-950/80 border-red-500/60 text-red-300 hover:bg-red-900/90 shadow-red-950/50'
              : 'bg-emerald-950/80 border-emerald-500/60 text-emerald-300 hover:bg-emerald-900/90 shadow-emerald-950/50'
          }`}
          title="Click to switch trading mode"
        >
          {isLive ? (
            <>
              <ShieldAlert className="w-4 h-4 text-red-400 animate-pulse" />
              <span className="tracking-wide uppercase font-bold text-red-400">🔴 LIVE MODE</span>
            </>
          ) : (
            <>
              <Shield className="w-4 h-4 text-emerald-400" />
              <span className="tracking-wide uppercase font-bold text-emerald-400">🟢 DEMO MODE</span>
            </>
          )}

          <ArrowRightLeft className="w-3 h-3 ml-1 text-slate-400 opacity-70 group-hover:opacity-100" />
        </button>
      </div>

      {/* Live Mode Warning & Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-red-500/50 rounded-xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <div className="flex items-center space-x-3 text-red-500">
              <AlertTriangle className="w-8 h-8 flex-shrink-0" />
              <h3 className="text-lg font-bold">Switch to Live Real-Money Trading?</h3>
            </div>

            <p className="text-sm text-slate-300 leading-relaxed">
              You are about to switch to <strong className="text-red-400">LIVE MODE</strong>. Trades will execute on your real Deriv account using real funds.
            </p>

            <div className="bg-red-950/40 border border-red-900/60 rounded-lg p-3 text-xs text-red-200">
              <strong>Risk Warning:</strong> Real trading carries substantial risk of loss. Ensure risk parameters (Daily Loss Limit, Max Trades) are set conservatively in Settings.
            </div>

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => executeSwitch('live')}
                disabled={isSwitching}
                className="px-4 py-2 text-xs font-bold rounded-lg bg-red-600 hover:bg-red-500 text-white transition-colors shadow-lg shadow-red-600/30"
              >
                {isSwitching ? 'Activating Live Mode...' : 'Confirm Live Mode'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModeIndicator;
